import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FormControl, Grid, Input, InputLabel, styled } from '@mui/material'
import { PuiCheckbox, PuiSelect, Utils } from '@pbt/pbt-ui-components'

import Link from '~/components/common/link/Link'
import { AlertBanner } from '~/components/elements/AlertBanner/AlertBanner'
import Typography from '~/components/elements/Typography/Typography'
import { fetchDocumentsByType } from '~/store/actions/documents'
import {
  getDefaultDischargeNotesTemplateId,
  getDefaultMedicalNotesTemplateId,
  setDefaultDischargeNotesTemplateId,
  setDefaultMedicalNotesTemplateId,
} from '~/store/duck/soapTemplateConfig'
import {
  getDocumentTypes,
  getStaticSoapWidget,
} from '~/store/reducers/constants'
import {
  getDocumentsIsReceiving,
  getDocumentsTypedList,
  getMultipleDocuments,
} from '~/store/reducers/documents'
import { type SoapTemplateTab, SoapWidgetName } from '~/types'

type NotesWidgetConfigDetailsProps = {
  allTabs?: SoapTemplateTab[]
  onNavigateAway?: () => void
  onUpdateWidgetConfig: () => void
  tabId?: string
}

export type NotesWidgetConfigDetailsHandle = {
  validate: () => boolean
}

const PaddedGrid = styled(Grid)(({ theme }) => ({
  padding: `0 ${theme.spacing(3)} ${theme.spacing(1)}`,
}))

const ItemGrid = styled(Grid)(({ theme }) => ({
  paddingBottom: `${theme.spacing(2)}`,
}))

const NotesWidgetConfigDetails = forwardRef<
  NotesWidgetConfigDetailsHandle,
  NotesWidgetConfigDetailsProps
>(
  (
    {
      onUpdateWidgetConfig,
      tabId,
      allTabs = [],
      onNavigateAway,
    }: NotesWidgetConfigDetailsProps,
    ref,
  ) => {
    const { t } = useTranslation('Admin')
    const dispatch = useDispatch()
    const DocumentTypes = useSelector(getDocumentTypes)

    const defaultMedicalNotesTemplateId = useSelector(
      getDefaultMedicalNotesTemplateId,
    )
    const defaultDischargeNotesTemplateId = useSelector(
      getDefaultDischargeNotesTemplateId,
    )

    const StaticSoapWidget = useSelector(getStaticSoapWidget)
    const notesWidgetId = Utils.findConstantIdByName(
      SoapWidgetName.NOTES_WIDGET,
      StaticSoapWidget,
    )

    const otherTabsWithNotesWidget = allTabs
      .filter((tab) =>
        tab.widgets.some((widget) => widget.soapWidgetId === notesWidgetId),
      )
      .filter((tab) => tab.id !== tabId)

    const isNotesWidgetInOtherTabs =
      otherTabsWithNotesWidget && otherTabsWithNotesWidget.length > 0

    const MedicalNotesTemplateType = Utils.findConstantIdByName(
      'Medical notes template',
      DocumentTypes,
    )

    const DischargeNotesTemplateType = Utils.findConstantIdByName(
      'Discharge notes template',
      DocumentTypes,
    )
    const isLoading = useSelector(getDocumentsIsReceiving)
    const documentsTypedList = useSelector(getDocumentsTypedList)
    const docs = useSelector(getMultipleDocuments(documentsTypedList))
    const medicalNotesTemplates = docs.filter(
      (doc) => doc.type === MedicalNotesTemplateType,
    )
    const dischargeNotesTemplates = docs.filter(
      (doc) => doc.type === DischargeNotesTemplateType,
    )

    const hasNoMedicalNotesTemplates =
      medicalNotesTemplates.length === 0 && !isLoading

    const hasNoDischargeNotesTemplates =
      dischargeNotesTemplates.length === 0 && !isLoading

    const templatesLoaded =
      medicalNotesTemplates.length > 0 && dischargeNotesTemplates.length > 0

    const [enableMedicalNotesDefault, setEnableMedicalNotesDefault] = useState(
      Boolean(defaultMedicalNotesTemplateId),
    )
    const [enableDischargeNotesDefault, setEnableDischargeNotesDefault] =
      useState(Boolean(defaultDischargeNotesTemplateId))

    const [medicalNotesError, setMedicalNotesError] = useState<string | null>(
      null,
    )
    const [dischargeNotesError, setDischargeNotesError] = useState<
      string | null
    >(null)

    useImperativeHandle(ref, () => ({
      validate: () => {
        const isMedicalNotesInvalid =
          enableMedicalNotesDefault && !defaultMedicalNotesTemplateId
        const isDischargeNotesInvalid =
          enableDischargeNotesDefault && !defaultDischargeNotesTemplateId

        if (isMedicalNotesInvalid) {
          setMedicalNotesError(
            t(
              'Admin:SOAP_CUSTOMIZATION.MEDICAL_NOTES_SET_DEFAULT_TEMPLATE_ERROR',
            ),
          )
        }
        if (isDischargeNotesInvalid) {
          setDischargeNotesError(
            t(
              'Admin:SOAP_CUSTOMIZATION.DISCHARGE_NOTES_SET_DEFAULT_TEMPLATE_ERROR',
            ),
          )
        }

        return !isMedicalNotesInvalid && !isDischargeNotesInvalid
      },
    }))

    useEffect(() => {
      if (defaultMedicalNotesTemplateId) {
        setMedicalNotesError(null)
      }
      if (defaultDischargeNotesTemplateId) {
        setDischargeNotesError(null)
      }
    }, [defaultMedicalNotesTemplateId, defaultDischargeNotesTemplateId])

    useEffect(() => {
      if (
        MedicalNotesTemplateType &&
        DischargeNotesTemplateType &&
        !templatesLoaded
      ) {
        dispatch(
          fetchDocumentsByType([
            MedicalNotesTemplateType,
            DischargeNotesTemplateType,
          ]),
        )
      }
    }, [DischargeNotesTemplateType, MedicalNotesTemplateType, templatesLoaded])

    return (
      <PaddedGrid>
        {isNotesWidgetInOtherTabs && (
          <AlertBanner
            content={t(
              'Admin:SOAP_CUSTOMIZATION.DEFAULT_TEMPLATE_SETTINGS_WARNING',
            )}
            title={t('Admin:SOAP_CUSTOMIZATION.DEFAULT_TEMPLATE_SETTINGS')}
            variant="info"
          />
        )}
        <Grid>
          <PuiCheckbox
            checked={enableMedicalNotesDefault}
            label={t(
              'Admin:SOAP_CUSTOMIZATION.MEDICAL_NOTES_SET_DEFAULT_TEMPLATE',
            )}
            onChange={() => {
              if (enableMedicalNotesDefault) {
                dispatch(setDefaultMedicalNotesTemplateId(null))
              }
              onUpdateWidgetConfig()
              setEnableMedicalNotesDefault(!enableMedicalNotesDefault)
            }}
          />
          {enableMedicalNotesDefault && (
            <ItemGrid>
              {hasNoMedicalNotesTemplates ? (
                <>
                  <Typography.ParagraphItalic>
                    {t('Admin:SOAP_CUSTOMIZATION.MEDICAL_NOTES_NO_TEMPLATES')}
                  </Typography.ParagraphItalic>
                  <Typography.Paragraph>
                    <Link
                      to="/admin/catalog/documents/create"
                      onClick={onNavigateAway}
                    >
                      {t(
                        'Admin:SOAP_CUSTOMIZATION.MEDICAL_NOTES_NO_TEMPLATES_LINK',
                      )}
                    </Link>
                  </Typography.Paragraph>
                </>
              ) : (
                <FormControl fullWidth>
                  <InputLabel htmlFor="medical-notes-select">
                    {t(
                      'Admin:SOAP_CUSTOMIZATION.MEDICAL_NOTES_CHOOSE_DEFAULT_TEMPLATE',
                    )}
                  </InputLabel>
                  <PuiSelect
                    TooltipProps={{
                      message: medicalNotesError,
                      open: Boolean(medicalNotesError),
                    }}
                    error={Boolean(medicalNotesError)}
                    input={<Input id="medical-notes-select" />}
                    isLoading={isLoading}
                    items={medicalNotesTemplates}
                    label={t(
                      'Admin:SOAP_CUSTOMIZATION.MEDICAL_NOTES_CHOOSE_DEFAULT_TEMPLATE',
                    )}
                    value={defaultMedicalNotesTemplateId}
                    onChange={(event) => {
                      onUpdateWidgetConfig()
                      dispatch(
                        setDefaultMedicalNotesTemplateId(
                          String(event.target.value),
                        ),
                      )
                    }}
                  />
                </FormControl>
              )}
            </ItemGrid>
          )}
        </Grid>
        <Grid>
          <PuiCheckbox
            checked={enableDischargeNotesDefault}
            label={t(
              'Admin:SOAP_CUSTOMIZATION.DISCHARGE_NOTES_SET_DEFAULT_TEMPLATE',
            )}
            onChange={() => {
              if (enableDischargeNotesDefault) {
                dispatch(setDefaultDischargeNotesTemplateId(null))
              }
              onUpdateWidgetConfig()
              setEnableDischargeNotesDefault(!enableDischargeNotesDefault)
            }}
          />
          {enableDischargeNotesDefault && (
            <ItemGrid>
              {hasNoDischargeNotesTemplates ? (
                <>
                  <Typography.ParagraphItalic>
                    {t('Admin:SOAP_CUSTOMIZATION.DISCHARGE_NOTES_NO_TEMPLATES')}
                  </Typography.ParagraphItalic>
                  <Typography.Paragraph>
                    <Link
                      to="/admin/catalog/documents/create"
                      onClick={onNavigateAway}
                    >
                      {t(
                        'Admin:SOAP_CUSTOMIZATION.DISCHARGE_NOTES_NO_TEMPLATES_LINK',
                      )}
                    </Link>
                  </Typography.Paragraph>
                </>
              ) : (
                <FormControl fullWidth>
                  <InputLabel htmlFor="discharge-notes-select">
                    {t(
                      'Admin:SOAP_CUSTOMIZATION.DISCHARGE_NOTES_CHOOSE_DEFAULT_TEMPLATE',
                    )}
                  </InputLabel>
                  <PuiSelect
                    TooltipProps={{
                      message: dischargeNotesError,
                      open: Boolean(dischargeNotesError),
                    }}
                    input={<Input id="discharge-notes-select" />}
                    isLoading={isLoading}
                    items={dischargeNotesTemplates}
                    label={t(
                      'Admin:SOAP_CUSTOMIZATION.DISCHARGE_NOTES_CHOOSE_DEFAULT_TEMPLATE',
                    )}
                    value={defaultDischargeNotesTemplateId}
                    onChange={(event) => {
                      onUpdateWidgetConfig()
                      dispatch(
                        setDefaultDischargeNotesTemplateId(
                          String(event.target.value),
                        ),
                      )
                    }}
                  />
                </FormControl>
              )}
            </ItemGrid>
          )}
        </Grid>
      </PaddedGrid>
    )
  },
)

export default NotesWidgetConfigDetails
